import { useState, useCallback } from 'react'
import { ColDef, FilterChangedEvent, SortChangedEvent } from 'ag-grid-community'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'

export const useTicketTypeFilters = (gridOptions?: any) => {
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )

  /**
   * フィルター変更時のハンドラ
   */
  const handleFilterChanged = useCallback((event: FilterChangedEvent) => {
    const filterModel = event.api.getFilterModel()
    const filteredCols: ColDef[] = []

    Object.keys(filterModel).forEach(colId => {
      const colDef = event.api.getColumnDef(colId)
      if (colDef) {
        filteredCols.push(colDef)
      }
    })

    setFilteredColumns(filteredCols)

    // フィルターまたはソートが適用されている場合はドラッグを無効化
    const columnState = event.columnApi?.getColumnState() || []
    const hasSortedColumns = columnState.some(col => col.sort != null)
    const hasFilters = Object.keys(filterModel).length > 0
    event.api.setSuppressRowDrag(hasSortedColumns || hasFilters)
  }, [])

  /**
   * フィルター削除
   */
  const handleDeleteFilteredColumn = useCallback((columnDef: ColDef) => {
    if (!columnDef || !gridOptions?.api) return

    const filterModel = gridOptions.api.getFilterModel()
    const colId = columnDef.colId || columnDef.field || ''
    delete filterModel[colId]
    gridOptions.api.setFilterModel(filterModel)
  }, [])

  /**
   * 全フィルターリセット
   */
  const resetFilters = useCallback(() => {
    if (!gridOptions?.api) return
    gridOptions.api.setFilterModel([])
  }, [])

  /**
   * ソート変更時のハンドラ
   */
  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    // ソート中の列を取得
    const sortedColumns = event.columnApi
      .getColumnState()
      .filter(col => !!col.sort)
      .map(col => event.api.getColumnDef(col.colId))
      .filter((v): v is ColDef => v !== null)

    // ColumnStateからソート情報を取得
    const columnState = event.columnApi?.getColumnState() || []
    const sortedColumnState: { [colId: string]: any } = {}
    columnState.forEach(state => {
      if (state.sort) {
        sortedColumnState[state.colId] = state
      }
    })

    // SortedColumnStateの配列を作成
    const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => ({
      colId: col.colId,
      field: col.field,
      headerName: col.headerName,
      sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
    }))

    setSortColumnsState(sortedColumnsState)

    // ドラッグアンドドロップの無効化制御（ソートまたはフィルター適用時）
    const filterModel = event.api.getFilterModel()
    const hasFilters = Object.keys(filterModel).length > 0
    event.api.setSuppressRowDrag(0 < sortedColumns.length || hasFilters)
  }, [])

  /**
   * ソート列削除
   */
  const handleDeleteSortedColumn = useCallback((column: string | ColDef) => {
    if (!gridOptions?.columnApi) return

    const colId = typeof column === 'string' ? column : column.colId || ''
    gridOptions.columnApi.applyColumnState({
      state: [{ colId, sort: null }],
    })
  }, [])

  /**
   * 全ソート削除
   */
  const handleDeleteSortedAllColumns = useCallback(() => {
    if (!gridOptions?.columnApi) return

    gridOptions.columnApi.applyColumnState({
      defaultState: { sort: null },
    })
  }, [])

  /**
   * ソート状態変更
   */
  const handleChangeSortColumnState = useCallback(
    (column: string | ColDef, sort: 'asc' | 'desc' | null) => {
      if (!gridOptions?.columnApi) return

      const colId = typeof column === 'string' ? column : column.colId || ''
      gridOptions.columnApi.applyColumnState({
        state: [{ colId, sort }],
      })
    },
    []
  )

  return {
    filteredColumns,
    sortColumnsState,
    handleFilterChanged,
    handleDeleteFilteredColumn,
    resetFilters,
    handleSortChanged,
    handleDeleteSortedColumn,
    handleDeleteSortedAllColumns,
    handleChangeSortColumnState,
  }
}
